/**
 * Customers API service
 * Mirrors the canonical Customer shape from src/services/customers.ts in the API.
 */

import { apiClient } from './api';
import { createResourceApi } from './createResourceApi';

export type CustomerStatus = 'active' | 'inactive' | 'suspended';

export interface CustomerMetrics {
  total_revenue: number;
  order_count: number;
  last_order_at: string | null;
  outstanding_balance: number;
  avg_order_value: number;
}

export interface Customer {
  id: number;
  email: string;
  full_name: string;
  is_active: boolean;
  customer_status: CustomerStatus | null;

  store_name: string | null;
  llc_name: string | null;
  contact_phone: string | null;
  address_line1: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;

  sales_rep_id: number | null;
  sales_rep_name: string | null;

  pricing_tier_id: number | null;
  pricing_tier_name: string | null;

  /** VP-CUST-001: real per-customer credit limit (users.credit_limit). */
  credit_limit: number;

  metrics: CustomerMetrics;

  created_at: string;
  updated_at: string;
}

export interface UpdateCustomerInput {
  full_name?: string;
  is_active?: boolean;
  customer_status?: CustomerStatus;
  contact_phone?: string;
  address_line1?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  store_name?: string;
  llc_name?: string;
  sales_rep_id?: number | null;
  pricing_tier_id?: number | null;
}

export interface CustomerSummary {
  total: number;
  active: number;
  inactive: number;
  suspended: number;
  weekly_revenue: number;
  avg_order_value: number;
}

export const customersApi = createResourceApi<Customer, never, UpdateCustomerInput>(
  '/customers',
  { updateMethod: 'patch' }
);

export async function getCustomerSummary(): Promise<CustomerSummary> {
  return apiClient.get<CustomerSummary>('/customers/summary');
}

export async function getMyCustomerProfile(): Promise<Customer> {
  return apiClient.get<Customer>('/customers/me');
}

/**
 * Maps a backend pricing tier name to the legacy three-bucket label
 * the customer table UI expects (Gold / Silver / Bronze).
 * Falls back to 'Bronze' so the UI always renders.
 */
export function mapTierLabel(pricingTierName: string | null | undefined): 'Gold' | 'Silver' | 'Bronze' {
  if (!pricingTierName) return 'Bronze';
  const lower = pricingTierName.toLowerCase();
  if (lower.includes('gold') || lower.includes('premium') || lower.includes('a')) return 'Gold';
  if (lower.includes('silver') || lower.includes('standard') || lower.includes('b')) return 'Silver';
  return 'Bronze';
}

/**
 * Derives a coarse health bucket from outstanding balance + revenue.
 * Pure UI helper — no backend column.
 */
export function deriveHealth(c: Customer): 'Green' | 'Yellow' | 'Red' {
  const out = c.metrics.outstanding_balance;
  const rev = c.metrics.total_revenue;
  if (out === 0 && rev > 0) return 'Green';
  if (rev > 0 && out / rev < 0.4) return 'Yellow';
  return 'Red';
}
